<template>
  <div id="create-campaign">
    <div class="subheader py-2 py-lg-6 subheader-transparent" id="kt_subheader">
      <div
        class="
          container
          d-flex
          align-items-center
          justify-content-between
          flex-wrap flex-sm-nowrap
        "
      >
        <!--begin::Info-->
        <div class="d-flex align-items-center flex-wrap mr-1">
          <!--begin::Page Heading-->
          <div class="d-flex align-items-baseline flex-wrap mr-5">
            <!--begin::Page Title-->
            <h5 class="text-dark font-weight-bold my-1 mr-5">
              {{
                $t("messages.generic.crud.create", {
                  module: $t("messages.campaigns.form.title").toLowerCase()
                })
              }}
            </h5>
            <!--end::Page Title-->
          </div>
          <!--end::Page Heading-->
        </div>
        <div class="d-flex align-items-center">
          <b-button
            tag="a"
            @click="$router.push({ name: 'ListCampaigns' })"
            size="sm"
            pill
            variant="light-primary"
            class="font-weight-bolder text-uppercase mr-2"
            >{{ $t("messages.tables.back") }}</b-button
          >
        </div>
        <!--end::Info-->
      </div>
    </div>
    <transition-group
      enter-active-class="animate__animated animate__slideInLeft"
    >
      <b-card
        class="card-custom text-left gutter-b"
        v-for="(group, index) in fields"
        :key="group.name"
        v-show="actualStep >= group.step"
        no-body
      >
        <div class="card-header">
          <h3 class="card-title">
            {{ index + 1 }}.
            {{ $t(`messages.campaigns.form.groups.${group.name}`) }}
          </h3>
        </div>
        <div class="card-body">
          <b-form class="form" @submit.stop.prevent="save(form)">
            <b-row>
              <b-col cols="12">
                <b-alert :show="error !== false" variant="danger">
                  {{ $t(`messages.campaigns.form.errors.${error}`) }}
                </b-alert>
                <b-alert :show="groupHasError(group)" variant="danger">
                  {{ $t(`messages.campaigns.form.errors.generic`) }}
                </b-alert>
              </b-col>
              <b-col
                :xl="field.type !== 'slider' && field.type !== 'radio' ? 6 : 12"
                :class="field.clear ? 'mr-1' : ''"
                v-for="field in groupFields(group.fields)"
                :key="field.name"
              >
                <b-form-group
                  style="position: relative"
                  :state="hasError(field, true)"
                  :invalid-feedback="hasError(field)"
                  :label="
                    field.hidden || field.type === 'radio'
                      ? ''
                      : $t(`messages.campaigns.form.${field.name}.title`) +
                        ` ${field.required ? '*' : ''}`
                  "
                  :label-for="field.name"
                  :description="
                    field.hidden || field.type === 'radio'
                      ? ''
                      : $t(`messages.campaigns.form.${field.name}.description`)
                  "
                >
                  <b-row v-if="field.type === 'slider'">
                    <b-col cols="12">
                      <slider
                        :rangeData="range"
                        :id="field.name"
                        theme="light"
                        @changed="changeValue"
                      />
                    </b-col>
                  </b-row>
                  <Multiselect
                    v-else-if="field.type === 'select'"
                    :id="field.name"
                    v-model="form[field.name]"
                    :options="
                      field.options
                        ? field.options.length > 0
                          ? field.options
                          : []
                        : []
                    "
                    :required="field.required"
                    :searchable="true"
                    :closeOnSelect="!field.multiple"
                    label="text"
                    trackBy="value"
                    :value="field.default ? field.default : {}"
                    :multiple="field.multiple || false"
                    :selectLabel="$t(`messages.campaigns.form.default.select`)"
                    :show-labels="field.multiple || false"
                    :deselectLabel="
                      $t(`messages.campaigns.form.default.deselect`)
                    "
                    :selectedLabel="
                      $t(`messages.campaigns.form.default.selected`)
                    "
                    :placeholder="
                      $t(`messages.campaigns.form.${field.name}.placeholder`)
                    "
                    :style="
                      field.multiple ? 'width: 80%;display: inline-block;' : ''
                    "
                  >
                    <template slot="option" slot-scope="props">
                      <div class="option__desc">
                        <span
                          class="option__title"
                          v-html="props.option.text"
                        />
                      </div>
                    </template>
                  </Multiselect>
                  <b-row v-else-if="field.type === 'datetime'">
                    <b-col lg="6">
                      <b-form-datepicker
                        class="form-control"
                        :id="`${field.name}_date`"
                        :required="field.required"
                        v-model="form[`${field.name}_date`]"
                        today-button
                        reset-button
                        v-bind="labels[locale] || {}"
                        :start-weekday="1"
                        close-button
                        :locale="locale"
                        :min="today"
                      />
                    </b-col>
                    <b-col lg="6">
                      <b-form-timepicker
                        class="form-control"
                        :minutes-step="15"
                        :id="`${field.name}_time`"
                        :required="field.required"
                        v-model="form[`${field.name}_time`]"
                        now-button
                        reset-button
                        v-bind="labels[locale] || {}"
                        :start-weekday="1"
                        close-button
                        :locale="locale"
                      />
                    </b-col>
                  </b-row>
                  <b-input
                    v-else-if="field.type === 'number'"
                    :id="field.name"
                    :required="field.required"
                    v-model="form[field.name]"
                    :min="field.min || 0"
                    :placeholder="
                      $t(`messages.campaigns.form.${field.name}.placeholder`)
                    "
                    type="number"
                  />
                  <b-col
                    cols="12"
                    v-else-if="field.type === 'radio'"
                    class="mb-8 text-center"
                  >
                    <b-button
                      v-for="option in field.options"
                      :key="option.value"
                      class="font-weight-bold mx-4"
                      size="md"
                      :variant="
                        form[field.name] === option.value
                          ? 'primary'
                          : 'light-success'
                      "
                      pill
                      @click="form[field.name] = option.value"
                    >
                      {{ option.text }}
                    </b-button>
                  </b-col>
                  <b-input
                    v-else
                    autocomplete="off"
                    :id="field.name"
                    :required="field.required"
                    v-model="form[field.name]"
                    :placeholder="
                      $t(`messages.campaigns.form.${field.name}.placeholder`)
                    "
                    :hidden="field.hidden ? true : false"
                  />
                  <b-button
                    v-if="field.multiple"
                    class="py-3 btn-hover-dark font-weight-bold"
                    variant="secondary"
                    @click="form[field.name] = field.options"
                    style="
                      width: 15%;
                      right: 0;
                      display: inline-block;
                      position: absolute;
                      margin-top: 0;
                    "
                  >
                    {{ $t("messages.campaigns.form.addAll") }}
                  </b-button>
                </b-form-group>
              </b-col>
              <b-col
                cols="2"
                class="mt-8"
                v-if="
                  group.name === 'template' &&
                    form.schedule_type !== 'automatic' &&
                    form.template_type === 'standard'
                "
              >
                <b-button
                  @click="getCampaignTemplate('mail')"
                  variant="info"
                  pill
                  v-if="form.campaignTypeId !== null"
                  >{{ $t("messages.tables.showEmail") }}
                </b-button>
              </b-col>
              <b-col
                cols="2"
                class="mt-8"
                v-if="
                  group.name === 'template' &&
                    form.schedule_type !== 'automatic' &&
                    form.template_type === 'standard'
                "
              >
                <b-button
                  v-if="form.campaignTypeId !== null"
                  @click="getCampaignTemplate('landing')"
                  variant="info"
                  pill
                  >{{ $t("messages.tables.showLanding") }}
                </b-button>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6" class="mt-4" v-if="group.sendButton">
                <b-button
                  type="submit"
                  variant="primary"
                  size="lg"
                  class="font-weight-bolder"
                >
                  {{ $t("messages.campaigns.form.save") }}
                </b-button>
              </b-col>
              <b-col
                cols="6"
                class="mt-4"
                v-else-if="!group.sendButton && actualStep === group.step"
              >
                <b-button
                  variant="primary"
                  size="lg"
                  class="font-weight-bolder"
                  @click="nextStep"
                >
                  {{ $t("messages.campaigns.form.next") }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </div>
      </b-card>
    </transition-group>
    <b-card
      no-body
      v-show="showmail || showlanding"
      class="card-custom gutter-b mt-9"
    >
      <iframe
        ref="landingiframe"
        class="templateiframe"
        v-show="showlanding"
        style="padding: 50px 0; min-height: 400px"
      />
      <iframe
        ref="mailiframe"
        class="templateiframe"
        v-show="showmail"
        style="padding: 50px 0; min-height: 400px"
      />
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import _ from "lodash";
import moment from "moment-timezone";
import { validate } from "@/utils/validators";

import { getTemplate, getDomains, getSmtp } from "@/api/campaigns.api";
import Multiselect from "vue-multiselect";
import axios from "axios";
import Slider from "@/components/form/Slider.component";

export default {
  name: "CreateCampaign",
  data() {
    return {
      locale: localStorage.getItem("language"),
      sendNow: true,
      errors: [],
      oldForm: {},
      surveysOptions: [],
      template: {},
      showmail: false,
      showlanding: false,
      domains: [],
      smtp: [],
      scheduling: "manual",
      actualStep: 1,
      form: {
        name: "",
        departments: [],
        campaignTypeId: null,
        timezone: {
          text: moment.tz.guess(),
          value: moment.tz.guess()
        },
        category: false,
        level: false,
        startDate_date: moment().format("YYYY-MM-DD"),
        startDate_time: moment().format("HH:mm"),
        endDate_date: "",
        endDate_time: "",
        sendByDate_date: "",
        sendByDate_time: "",
        schedule_type: "",
        template_type: "",
        landing_url: "",
        sender_profile: "",
        totalCampaigns: 0,
        frecuency: false,
        surveyTypeId: false
      },
      range: [
        {
          name: this.$t("messages.campaigns.form.slider.week"),
          color: "#900c3f",
          value: "+1week"
        },
        {
          name: this.$t("messages.campaigns.form.slider.fifteen"),
          color: "#c70039",
          value: "+15days"
        },
        {
          name: this.$t("messages.campaigns.form.slider.monthly"),
          color: "#ff5733",
          value: "+1month"
        },
        {
          name: this.$t("messages.campaigns.form.slider.bimonth"),
          color: "#ffc300",
          value: "+2months"
        },
        {
          name: this.$t("messages.campaigns.form.slider.trimonth"),
          color: "#ea168e",
          value: "+3months"
        }
      ],
      labels: {
        es: {
          labelPrevDecade: "Década anterior",
          labelPrevYear: "Próximo año",
          labelPrevMonth: "Año anterior",
          labelCurrentMonth: "Mes actual",
          labelNextMonth: "Próximo mes",
          labelNextYear: "Próximo año",
          labelNextDecade: "Próxima década",
          labelToday: "Hoy",
          labelSelected: "Fecha seleccionada",
          labelNoDateSelected: "Fecha no seleccionada",
          labelNoTimeSelected: "Hora no seleccionada",
          labelCalendar: "Calendario",
          labelNav: "Navegación",
          labelHelp: "Ayuda",
          labelResetButton: "Reiniciar",
          labelTodayButton: "Hoy",
          labelCloseButton: "Cerrar",
          labelNowButton: "Ahora",
          labelHours: "Horas",
          labelMinutes: "Minutos",
          labelSeconds: "Segundos",
          labelIncrement: "Aumentar",
          labelDecrement: "Disminuir"
        }
      }
    };
  },
  components: {
    Multiselect,
    Slider
  },
  computed: {
    ...mapGetters("Phishing", [
      "isCampaignsActive",
      "isCampaignCreated",
      "error",
      "types",
      "departments"
    ]),
    ...mapGetters("Constants", ["isConstantsActive", "getConstant"]),
    ...mapGetters("Auth", ["hasModule"]),
    today() {
      return moment()
        .tz(this.form.timezone.value)
        .format();
    },
    timezones() {
      var result = [];
      _.each(moment.tz.names(), name => {
        result.push({
          text: name,
          value: name
        });
      });
      return result;
    },
    categories() {
      var result = [];
      _.each(
        _.groupBy(this.types, survey => survey.campaignCategoryId),
        survey => {
          let unique = _.first(survey);
          if (
            parseInt(unique.surveyTypeId) ===
              parseInt(this.form.surveyTypeId) &&
            (this.form.schedule_type !== "automatic" ||
              parseInt(unique.campaignCategoryId) !== 5) //TODO: To be removed
          ) {
            result.push({
              text: unique.campaignCategory,
              value: unique.campaignCategoryId
            });
          }
        }
      );
      return _.orderBy(result, element => element.text);
    },
    allLevels() {
      var result = [];
      _.each(
        _.groupBy(this.types, survey => survey.campaignLevelId),
        survey => {
          let unique = _.first(survey);
          result.push({
            text: unique.campaignLevel,
            value: unique.campaignLevelId
          });
        }
      );
      return result;
    },
    levels() {
      var result = [];
      if (
        this.form.schedule_type !== "automatic" &&
        this.form.template_type !== "neurophishing"
      ) {
        _.each(
          _.groupBy(
            _.filter(
              this.types,
              type =>
                this.form.category &&
                type.campaignCategoryId === this.form.category.value &&
                parseInt(type.surveyTypeId) === parseInt(this.form.surveyTypeId)
            ),
            survey => survey.campaignLevel
          ),
          survey => {
            let unique = _.first(survey);
            result.push({
              text: unique.campaignLevel,
              value: unique.campaignLevelId
            });
          }
        );
      } else {
        _.each(
          _.groupBy(this.types, survey => survey.campaignLevelId),
          survey => {
            let unique = _.first(survey);
            result.push({
              text: unique.campaignLevel,
              value: unique.campaignLevelId
            });
          }
        );
      }
      return result;
    },
    formatedDepartments() {
      var result = [];
      _.each(this.departments, department => {
        result.push({
          text: department.name,
          value: department.stakeholderId
        });
      });
      return result;
    },
    fields() {
      var result = [
        {
          name: "generic",
          step: 1,
          fields: [
            {
              name: "name",
              required: true,
              type: "input"
            },
            {
              name: "departments",
              required: true,
              type: "select",
              options: this.formatedDepartments || [],
              multiple: true
            }
          ]
        },
        {
          name: "schedule",
          step: 2,
          fields: [
            {
              name: "schedule_type",
              required: true,
              type: "radio",
              options: [
                {
                  text: this.$t("messages.campaigns.form.buttons.sendNow"),
                  value: "now"
                },
                {
                  text: this.$t("messages.campaigns.form.buttons.programSend"),
                  value: "program"
                },
                {
                  text: this.$t(
                    "messages.campaigns.form.buttons.automaticSend"
                  ),
                  value: "automatic"
                }
              ]
            },
            {
              name: "timezone",
              required: true,
              type: "select",
              options: this.timezones || [],
              schedule_type: ["program"]
            },
            {
              name: "startDate",
              required: true,
              type: "datetime",
              schedule_type: ["program", "automatic"]
            },
            {
              name: "sendByDate",
              type: "datetime",
              schedule_type: ["program"]
            },
            {
              name: "endDate",
              type: "datetime",
              schedule_type: ["program"]
            },
            {
              name: "totalCampaigns",
              required: true,
              type: "number",
              min: 1,
              schedule_type: ["automatic"]
            },
            {
              name: "frecuency",
              required: true,
              type: "slider",
              schedule_type: ["automatic"]
            }
          ]
        },
        {
          name: "template",
          step: 3,
          sendButton: true,
          fields: [
            {
              name: "template_type",
              required: true,
              type: "radio",
              options: [
                {
                  text: this.$t("messages.campaigns.form.buttons.standard"),
                  value: "standard"
                }
              ]
            },
            {
              name: "category",
              required: true,
              type: "select",
              options: this.categories || [],
              schedule_type: ["now", "program"],
              template_type: ["standard"]
            },
            {
              name: "level",
              required: true,
              type: "select",
              options: this.levels || [],
              schedule_type: ["now", "program"],
              template_type: ["standard"]
            },
            {
              name: "level",
              required: true,
              multiple: true,
              type: "select",
              options: this.allLevels || [],
              schedule_type: ["automatic"],
              template_type: ["standard"]
            },
            {
              name: "campaignTypeId",
              required: true,
              type: "select",
              clear: true,
              options: this.surveysOptions,
              schedule_type: ["now", "program"],
              template_type: ["standard", "neurophishing"]
            },
            {
              name: "landing_url",
              required: true,
              type: "select",
              options: this.domains || [],
              schedule_type: ["now", "program"],
              template_type: ["standard"]
            },
            {
              name: "sender_profile",
              required: true,
              type: "select",
              options: this.smtp || [],
              schedule_type: ["now", "program"],
              template_type: ["standard"]
            }
          ]
        }
      ];
      if (this.hasModule(["neurogaitas"])) {
        var indexOfTemplate = _.findIndex(
          result,
          element => element.name === "template"
        );
        var indexOfType = _.findIndex(
          result[indexOfTemplate].fields,
          element => element.name === "template_type"
        );

        result[indexOfTemplate].fields[indexOfType].options.push({
          text: this.$t("messages.campaigns.form.buttons.neurophishing"),
          value: "neurophishing"
        });
      }
      return result;
    }
  },
  methods: {
    ...mapActions("Phishing", [
      "loadPhishing",
      "insertCampaign",
      "insertScheduledCampaign"
    ]),
    changeValue(value, field) {
      this.form[field] = value;
    },
    mustShowField(field) {
      return (
        (field.schedule_type === undefined ||
          _.indexOf(field.schedule_type, this.form.schedule_type) >= 0) &&
        (field.template_type === undefined ||
          _.indexOf(field.template_type, this.form.template_type) >= 0)
      );
    },
    groupFields(fields) {
      return _.filter(fields, field => this.mustShowField(field));
    },
    groupHasError(group) {
      var result = false;
      _.each(group.fields, field => {
        if (_.find(this.errors, error => error.name === field.name)) {
          result = true;
        }
      });
      return result;
    },
    nextStep() {
      var self = this;
      var visibleFields = _.filter(
        this.fields,
        group => group.step <= this.actualStep
      );
      visibleFields = _.each(
        visibleFields,
        group =>
          (group.fields = _.filter(group.fields, field =>
            self.mustShowField(field)
          ))
      );
      var errors = { valid: true };
      if (visibleFields.length > 0) {
        errors = validate(visibleFields, this.form);
      }
      if (errors.valid) {
        this.actualStep++;
      } else {
        this.errors = errors.errors;
      }
    },
    hasError(field, boolean = false) {
      var result = null;
      var finder = _.find(this.errors, error => error.name === field.name);
      var found = !_.isEmpty(finder);

      if (found) {
        if (boolean) result = false;
        else if (!finder.error) {
          var multiple = 1;
          if (field.type === "datetime") multiple = 2;
          if (field.type === "number" && this.form[field.name] < field.min) {
            result = this.$t("messages.campaigns.form.errors.minNumber", {
              field: _.toLower(
                this.$t(
                  `messages.campaigns.form.${
                    _.find(this.errors, error => error.name === field.name).name
                  }.title`
                )
              ),
              value: field.min
            });
          } else {
            result = this.$tc(
              "messages.campaigns.form.errors.isRequired",
              {
                field: _.toLower(
                  this.$t(
                    `messages.campaigns.form.${
                      _.find(this.errors, error => error.name === field.name)
                        .name
                    }.title`
                  )
                )
              },
              multiple
            );
          }
        } else
          result = this.$t(`messages.campaigns.form.errors.${finder.error}`, {
            field: this.$t(
              `messages.campaigns.form.${
                _.find(this.errors, error => error.name === field.name).name
              }.title`
            ),
            checker: this.$t(
              `messages.campaigns.form.${
                _.find(this.errors, error => error.name === field.name).checker
              }.title`
            )
          });
      }

      return result;
    },
    save(form) {
      this.errors = [];
      var visibleFields = _.each(
        this.fields,
        group =>
          (group.fields = _.filter(group.fields, field =>
            this.mustShowField(field)
          ))
      );
      var validation = validate(visibleFields, form);
      var startDate = form.startDate_date + " " + form.startDate_time;
      var sendByDate = form.sendByDate_date + " " + form.sendByDate_time;
      var endDate = form.endDate_date + " " + form.endDate_time;

      var startDateMoment = moment(startDate);
      var sendByDateMoment = moment(sendByDate);
      var endDateMoment = moment(endDate);
      if (validation.valid) {
        if (startDateMoment.isValid()) {
          if (
            !_.isEmpty(sendByDate) &&
            sendByDateMoment.isSameOrBefore(startDateMoment)
          ) {
            this.errors.push({
              name: "sendByDate",
              error: "isAfter",
              checker: "startDate"
            });
          }

          if (!_.isEmpty(endDate)) {
            if (!_.isEmpty(sendByDate)) {
              if (endDateMoment.isSameOrBefore(startDateMoment)) {
                this.errors.push({
                  name: "endDate",
                  error: "isAfter",
                  checker: "startDate"
                });
              } else {
                if (endDateMoment.isSameOrBefore(sendByDateMoment)) {
                  this.errors.push({
                    name: "endDate",
                    error: "isAfter",
                    checker: "sendByDate"
                  });
                }
              }
            }
          }
        } else {
          this.errors.push({
            name: "startDate"
          });
        }
        if (_.isEmpty(this.errors)) {
          this.$swal
            .mixin({
              confirmButtonText: this.$t("messages.campaigns.form.confirm"),
              cancelButtonText: this.$t("messages.campaigns.form.cancel"),
              showCancelButton: true,
              progressSteps: ["1", "2", "3"]
            })
            .queue([
              this.$t("messages.campaigns.form.steps.first"),
              this.$t("messages.campaigns.form.steps.second"),
              this.$t("messages.campaigns.form.steps.third")
            ])
            .then(result => {
              if (result.value) {
                this.$swal
                  .fire({
                    title: this.$t("messages.campaigns.form.allDone"),
                    showCancelButton: true,
                    cancelButtonText: this.$t("messages.campaigns.form.cancel"),
                    confirmButtonText: this.$t("messages.campaigns.form.launch")
                  })
                  .then(result => {
                    if (result) {
                      if (form.schedule_type !== "automatic") {
                        this.insertCampaign(form);
                      } else {
                        form.category = this.categories;
                        this.insertScheduledCampaign(form);
                      }
                    }
                  });
              }
            });
        }
      } else {
        this.errors = validation.errors;
      }
    },
    calculateSurveysOptions() {
      var result = [];
      var types = this.form.category
        ? _.filter(
            this.types,
            type =>
              this.form.level &&
              parseInt(type.campaignLevelId) ===
                parseInt(this.form.level.value) &&
              parseInt(type.campaignCategoryId) ===
                parseInt(this.form.category.value) &&
              parseInt(type.surveyTypeId) === parseInt(this.form.surveyTypeId)
          )
        : [];
      types =
        this.form.template_type === "neurophishing"
          ? _.filter(
              this.types,
              type =>
                parseInt(type.surveyTypeId) === parseInt(this.form.surveyTypeId)
            )
          : types;
      _.each(_.each(types), survey => {
        result.push({
          text: survey.campaignName,
          category: survey.campaignType,
          value: survey.campaignTypeId
        });
      });
      return result;
    },
    getCampaignTemplate(type) {
      if (this.form.campaignTypeId !== null) {
        this.showmail = false;
        this.showlanding = false;
        if (Object.keys(this.template).length === 0) {
          getTemplate(this.form.campaignTypeId.value).then(response => {
            this.template = response.data.records;
            if (response.data.records[type]) {
              const iFrameEl = this.$refs[type + "iframe"];
              const doc = iFrameEl.contentWindow.document;
              doc.open();
              doc.write(response.data.records[type].html);
              doc.close();
              this["show" + type] = true;
              iFrameEl.onload = () => {
                iFrameEl.style.height = doc.body.scrollHeight + 100 + "px";
                iFrameEl.style.overflow = "scroll";
              };
            }
          });
        } else {
          if (this.template[type]) {
            const iFrameEl = this.$refs[type + "iframe"];
            const doc = iFrameEl.contentWindow.document;
            doc.open();
            doc.write(this.template[type].html);
            doc.close();
            this["show" + type] = true;
            iFrameEl.onload = () => {
              iFrameEl.style.height = doc.body.scrollHeight + 100 + "px";
              iFrameEl.style.overflow = "scroll";
            };
          }
        }
      }
    }
  },
  mounted() {
    if (this.isConstantsActive && !this.isLoading) this.loadPhishing();
    axios.all([getDomains(), getSmtp()]).then(
      axios.spread((domainsResp, smtpResp) => {
        _.each(domainsResp.data.records, domain => {
          this.domains.push({
            text: domain,
            value: domain
          });
        });
        _.each(smtpResp.data.records, smtp => {
          this.smtp.push({
            text: smtp.email,
            value: smtp.name
          });
        });
      })
    );
  },
  watch: {
    isConstantsActive(value) {
      if (value === true) this.loadPhishing();
    },
    isCampaignCreated(value) {
      if (value === true) {
        this.$swal({
          title: this.$t("messages.campaigns.form.created.title"),
          text: this.$t("messages.campaigns.form.created.description", {
            module: this.$tc("messages.menu.controller.phishing.title", 1)
          }),
          icon: "success",
          showCancelButton: false,
          confirmButtonColor: "#ababab",
          confirmButtonText: this.$t(
            "messages.campaigns.form.created.createButton"
          )
        }).then(() => {
          this.$router.push({ name: "ListCampaigns" });
        });
      }
    },
    areUsersLoaded(value) {
      if (this.isConstantsActive && value === false) this.loadPhishing();
    },
    form: {
      deep: true,
      inmediate: true,
      handler(value) {
        this.surveysOptions = this.calculateSurveysOptions();
        if (
          (this.oldForm.category &&
            value.category &&
            this.oldForm.category.value !== value.category.value) ||
          (!value.category &&
            value.schedule_type !== "automatic" &&
            value.template_type !== "neurophishing")
        ) {
          this.form.level = false;
          this.form.campaignTypeId = null;
          this.showmail = false;
          this.showlanding = false;
          this.template = {};
        } else if (
          (this.oldForm.level &&
            value.level &&
            this.oldForm.level.value !== value.level.value) ||
          (!value.level && value.template_type !== "neurophishing")
        ) {
          this.form.campaignTypeId = null;
          this.showmail = false;
          this.showlanding = false;
          this.template = {};
        } else if (
          this.oldForm.campaignTypeId &&
          value.campaignTypeId &&
          this.oldForm.campaignTypeId.value !== value.campaignTypeId.value
        ) {
          this.showmail = false;
          this.showlanding = false;
          this.template = {};
        }

        if (value.schedule_type === "now") {
          this.form.startDate_date = moment().format("YYYY-MM-DD");
          this.form.startDate_time = moment().format("HH:mm:ss");
        }

        if (
          value.template_type === "neurophishing" &&
          this.hasModule(["neurogaitas"])
        ) {
          this.form.surveyTypeId = this.getConstant(
            "SURVEY_KYMATIO_NEUROPHISHING"
          );
          this.form.level = this.allLevels;
        } else if (value.template_type === "standard") {
          this.form.surveyTypeId = this.getConstant("SURVEY_KYMATIO_PHISHING");
        }

        if (!this.hasModule(["neurogaitas"])) {
          this.form.template_type = "standard";
        }

        this.oldForm = _.clone(value);
      }
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
.is-invalid .multiselect__tags {
  border: solid 1px #dc3545;
}
iframe.templateiframe {
  border: 0;
  pointer-events: none;
}
</style>
